import type { ProductionByProcedureRow } from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  type ChartAxisConfig,
} from '@utils/dashboardFinance.util'

/** Figma dashboard card — vertical axis in $K (e.g. 0, 4, 8, 12). */
export const PRODUCTION_BY_PROCEDURE_CARD_AXIS_STEP = 4
export const PRODUCTION_BY_PROCEDURE_CARD_AXIS_MAX = 12
export const PRODUCTION_BY_PROCEDURE_CARD_AXIS_TICKS = [0, 4, 8, 12] as const

/** Split API labels that were concatenated without separators (e.g. ConsultationsCosmetic…). */
export function splitProductionByProcedureCategoryLabel(
  text: string,
): string[] {
  const trimmed = text.trim()
  if (!trimmed) return []

  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
  }

  const parts = trimmed
    .split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])|(?<=-)(?=[A-Z])/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (parts.length >= 2 && trimmed.length >= 40) {
    return parts
  }

  return [trimmed]
}

export function formatProductionByProcedureCategoryLabel(text: string): string {
  return splitProductionByProcedureCategoryLabel(text).join(', ')
}

function categoryLabelFromApi(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry).trim())
      .filter(Boolean)
      .join(', ')
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map((entry) => String(entry).trim())
      .filter(Boolean)
      .join(', ')
  }
  return String(value).trim()
}

export function normalizeProductionByProcedureRow(
  row: ProductionByProcedureRow,
): ProductionByProcedureRow {
  const category = formatProductionByProcedureCategoryLabel(
    categoryLabelFromApi(row.category),
  )
  return { ...row, category }
}

export function normalizeProductionByProcedureRows(
  rows: ProductionByProcedureRow[] | undefined,
): ProductionByProcedureRow[] {
  if (!rows?.length) return []

  return [...rows]
    .map(normalizeProductionByProcedureRow)
    .sort((a, b) => b.production - a.production)
}

const PROCEDURE_CHART_ROW_HEIGHT_PX = 32
const PROCEDURE_CHART_BASE_PADDING_PX = 48

export function computeProductionByProcedureChartHeight(
  rowCount: number,
): number {
  if (rowCount <= 0) return 200
  return Math.max(
    200,
    rowCount * PROCEDURE_CHART_ROW_HEIGHT_PX + PROCEDURE_CHART_BASE_PADDING_PX,
  )
}

export function computeProductionByProcedureCategoryAxisWidth(
  labels: string[],
): number {
  if (!labels.length) return 140
  const maxChars = Math.max(...labels.map((label) => label.length))
  return Math.min(280, Math.max(140, Math.ceil(maxChars * 6.5) + 24))
}

/** Y-axis for Finance / Performance dashboard cards (top 4 categories, $K). */
export function computeProductionByProcedureCardAxis(
  values: number[],
): ChartAxisConfig {
  const maxDataValue = values.length ? Math.max(...values, 0) : 0
  if (maxDataValue <= PRODUCTION_BY_PROCEDURE_CARD_AXIS_MAX) {
    return {
      yDomain: [0, PRODUCTION_BY_PROCEDURE_CARD_AXIS_MAX],
      yTicks: [...PRODUCTION_BY_PROCEDURE_CARD_AXIS_TICKS],
    }
  }
  return computeChartAxis(values, 4)
}
